import React, { useEffect, useState } from "react";

import { Grid, MenuItem } from "@mui/material";
import { Option } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import { useAppState } from "../../../../../../context/StateContext";
import { CommodityCode } from "../../../../../../models/commodity.code";
import CommodityCodeService from "../../../../../../services/commodity.code.service";
import { ShipmentDetails, ShipmentStatus, ShipmentType } from "../../../../../../util/enums";
import { ShipmentObjects } from "../../../../../Booking/util";
import { Input, Select } from "../../../../../Form";
import AutoSuggest from "../../../../../Form/AutoSuggest";
import getShipmentOptions from "../../../../hooks/getShipmentOptions";
import { useShipmentContext } from "../../../../hooks/useShipmentContext";
import { BaseFieldProps, ShipmentKey } from "../../../../types";
import { SelectChangeEvent } from "@mui/material";

interface Props {
  viewOnly?: boolean;
  generateBaseFieldProps: (name: ShipmentKey) => BaseFieldProps<ShipmentKey>;
}

type LoadTypeOption = {
  [key: string]: Option[];
};
type IncotermOption = {
  [key: string]: Option[];
};
type StatusOption = {
  [key: string]: Option[];
};

const GeneralDetails = (props: Props) => {
  const { isFMVersion, currentUser } = useAppState();
  const [referenceField, setReferenceField] = useState<string>();

  const { generateBaseFieldProps } = props;

  const {
    shipment,
    handleUpdateShipment: update,
    handleChangeOptions,
    sequence,
    hsCodes,
  } = useShipmentContext();

  const handleUpdateShipment = (attribute: ShipmentKey, value: any) => {
    update(attribute, value);
    handleChangeOptions(ShipmentObjects.SHIPMENT);
  };

  useEffect(() => {
    if (shipment) {
      if (shipment?.containerId) setReferenceField(shipment?.containerId);
      else if (sequence) {
        handleUpdateShipment(
          ShipmentDetails.CONTAINER_ID,
          `${sequence?.prefix ? sequence.prefix : ""}${
            sequence?.nextValue ? sequence.nextValue : ""
          }`
        );
      }
    }
  }, [shipment, sequence]);

  useEffect(() => {
    if (!shipment?.statusShipment && !shipment?.shipmentTracking?.shipmentStatus) {
      handleUpdateShipment(ShipmentDetails.STATUS_SHIPMENT, ShipmentStatus.DRAFT);
    }
  }, []);

  const {
    typeOptions: shipmentTypeOptions,
    airLoadTypeOptions,
    roadLoadTypeOptions,
    seaLoadTypeOptions,
    railLoadTypeOptions,
    statusOptions,
    incotermOptions,
    shipTermsOptions,
    serviceTypeOptions,
    seaIncotermOptions,
    seaStatusOptions,
  } = getShipmentOptions(isFMVersion);

  const { t: translate } = useTranslation();

  const LoadTypeOptions: LoadTypeOption = {
    [ShipmentType.AIR]: airLoadTypeOptions,
    [ShipmentType.ROAD]: roadLoadTypeOptions,
    [ShipmentType.SEA]: seaLoadTypeOptions,
    [ShipmentType.RAIL]: railLoadTypeOptions,
  };

  const IncotermOptions: IncotermOption = {
    [ShipmentType.AIR]: incotermOptions,
    [ShipmentType.ROAD]: incotermOptions,
    [ShipmentType.SEA]: seaIncotermOptions,
    [ShipmentType.RAIL]: incotermOptions,
  };

  const StatusOptions: StatusOption = {
    [ShipmentType.AIR]: statusOptions,
    [ShipmentType.ROAD]: statusOptions,
    [ShipmentType.SEA]: seaStatusOptions,
    [ShipmentType.RAIL]: statusOptions,
  };
  const cleanStatus = (shipmentType: string) => {
    switch (shipmentType) {
      case ShipmentType.SEA:
        return { [ShipmentDetails.STATUS_SHIPMENT]: ShipmentStatus.DRAFT };
      default:
        return { [ShipmentDetails.STATUS_SHIPMENT]: null };
    }
  };

  return (
    <>
      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Select
          {...generateBaseFieldProps(ShipmentDetails.SHIPMENT_TYPE)}
          required
          disabled={!!shipment?.bookingId}
          defaultValue={""}
          displayEmpty
          placeholder={translate("select")}
          label={translate("mode")}
          value={shipment?.shipmentType}
          onChange={(event: SelectChangeEvent<any>) => {
            handleUpdateShipment("all", {
              [ShipmentDetails.SHIPMENT_TYPE]: event.target.value,
              [ShipmentDetails.SERVICE_TYPE]: null,
              [ShipmentDetails.PORT_OF_LOADING_ID]: null,
              [ShipmentDetails.PORT_OF_DISCHARGE_ID]: null,
              [ShipmentDetails.PORT_OF_DESTINATION_ID]: null,
              [ShipmentDetails.PORT_OF_RECEIPT_ID]: null,
              [ShipmentDetails.CONTAINERS]: [{}],
              [ShipmentDetails.CONTAINER]: null,
              [ShipmentDetails.ORIGIN]: null,
              [ShipmentDetails.DESTINATION]: null,
              [ShipmentDetails.ORIGIN_LOCATION]: null,
              [ShipmentDetails.DESTINATION_LOCATION]: null,
              [ShipmentDetails.CARRIER_ID]: null,
              [ShipmentDetails.CARRIER_SCAC]: null,
              ...(!shipment?.shipmentTrackingId ? cleanStatus(event.target.value) : {}),
            });
          }}
        >
          {shipmentTypeOptions?.map((option: Option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Input
          {...generateBaseFieldProps(ShipmentDetails.CONTAINER_ID)}
          label={`${translate("shipment")} #`}
          placeholder={`${translate("inputShipment")} #`}
          disabled={!!shipment?.id}
          value={referenceField}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setReferenceField(event.target.value)
          }
          onBlur={() => handleUpdateShipment(ShipmentDetails.CONTAINER_ID, referenceField)}
          required
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Select
          {...generateBaseFieldProps(ShipmentDetails.LOAD_TYPE)}
          defaultValue={""}
          displayEmpty
          label={translate("loadType")}
          required={!!shipment?.shipmentType && shipment?.shipmentType !== ShipmentType.RAIL}
          value={shipment?.[ShipmentDetails.LOAD_TYPE]}
          disabled={!shipment?.shipmentType || !LoadTypeOptions[shipment?.shipmentType]?.length}
          onChange={(event) => handleUpdateShipment(ShipmentDetails.LOAD_TYPE, event.target.value)}
        >
          <MenuItem value="">{translate("select")}</MenuItem>
          {shipment?.shipmentType &&
            LoadTypeOptions[shipment?.shipmentType]?.map((option: Option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
        </Select>
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Select
          {...generateBaseFieldProps(ShipmentDetails.STATUS_SHIPMENT)}
          defaultValue={""}
          displayEmpty
          required
          label={translate("status")}
          placeholder={translate("select")}
          value={
            shipment?.shipmentTracking?.shipmentStatus?.toLowerCase() ||
            shipment?.[ShipmentDetails.STATUS_SHIPMENT]
          }
          disabled={!!shipment?.shipmentTrackingId || !shipment?.shipmentType}
          onChange={(event) => {
            handleUpdateShipment(ShipmentDetails.STATUS_SHIPMENT, event.target.value);
          }}
        >
          {StatusOptions[shipment?.shipmentType ?? ShipmentType.SEA]?.map((option: Option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Select
          {...generateBaseFieldProps(ShipmentDetails.SERVICE_TYPE)}
          defaultValue={""}
          displayEmpty
          required
          label={translate("serviceType")}
          value={shipment?.[ShipmentDetails.SERVICE_TYPE]}
          onChange={(event) =>
            handleUpdateShipment(ShipmentDetails.SERVICE_TYPE, event.target.value)
          }
        >
          <MenuItem value="">{translate("select")}</MenuItem>
          {serviceTypeOptions?.map((option: Option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Select
          {...generateBaseFieldProps(ShipmentDetails.INCOTERM)}
          defaultValue={""}
          label={translate("incoterms")}
          displayEmpty
          required
          placeholder={translate("select")}
          value={shipment?.[ShipmentDetails.INCOTERM]}
          onChange={(event) => {
            handleUpdateShipment(ShipmentDetails.INCOTERM, event.target.value);
          }}
        >
          {shipment?.shipmentType &&
            IncotermOptions?.[shipment?.shipmentType]?.map((option: Option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
        </Select>
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Select
          {...generateBaseFieldProps(ShipmentDetails.SHIP_TERMS)}
          defaultValue={""}
          displayEmpty
          placeholder={translate("select")}
          value={shipment?.[ShipmentDetails.SHIP_TERMS]}
          onChange={(event) => {
            handleUpdateShipment(ShipmentDetails.SHIP_TERMS, event.target.value);
          }}
        >
          {shipTermsOptions?.map((option: Option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>

      <Grid item xs={12}>
        <AutoSuggest
          {...generateBaseFieldProps(ShipmentDetails.COMMODITY_HS_CODE_ID)}
          multiple
          label={translate("hsCommodityCode")}
          placeholder={""}
          getOptionLabel={(option: CommodityCode) => option?.hscode}
          getOptionSelected={(option: CommodityCode, value: CommodityCode) =>
            option.id === value.id
          }
          disabled
          value={hsCodes}
          renderOption={(props, option) => <div {...props}>`${option?.hscode} - ${option?.description}`</div>}
          // inputValue={
          //   shipment?.[ShipmentDetails.COMMODITY_CODE]
          //     ? shipment?.[ShipmentDetails.COMMODITY_CODE]?.hscode
          //     : ""
          // }
          onChange={(value: CommodityCode[]) => {
            const newValue = value?.[1] ?? value?.[0];

            if (newValue) {
              handleUpdateShipment("all", {
                [ShipmentDetails.COMMODITY_HS_CODE_ID]: newValue.id,
                [ShipmentDetails.COMMODITY]: newValue.hscode,
                [ShipmentDetails.COMMODITY_CODE]: newValue,
              });
            } else {
              handleUpdateShipment("all", {
                [ShipmentDetails.COMMODITY_HS_CODE_ID]: null,
                [ShipmentDetails.COMMODITY]: null,
                [ShipmentDetails.COMMODITY_CODE]: null,
              });
            }
          }}
          onSearch={(value) =>
            CommodityCodeService.getAll({
              companyId: currentUser?.companyId,
              sortBy: JSON.stringify([["section", "ASC"]]),
              searchByOr: value ? JSON.stringify([{ description: value }, { hscode: value }]) : "",
            })
          }
        />
      </Grid>
    </>
  );
};

export default GeneralDetails;
